import { GlobalLoader } from "@expert/common-ui";
import type React from "react";
import { useEffect, useMemo } from "react";
import { hasAuthParams, useAuth } from "react-oidc-context";
import { Navigate } from "react-router";
import { formatE2EUser, isE2EUser } from "../../e2e/utils/e2eUser";
import { type OIDCUser, SSO_STORAGE_KEY } from "../auth";
import { useExpertAssistStore, useLoggerWithContextStore } from "../state";
import { RoutePath } from "./RoutePath";

interface ProtectedRouteProps {
    children: React.ReactNode;
}

export function ProtectedRoute(props: ProtectedRouteProps) {
    const auth = useAuth();
    const { setToken, setExpertProfile } = useExpertAssistStore();
    const { loggerWithContext } = useLoggerWithContextStore();
    const localLogger = loggerWithContext.child({ module: "ProtectedRoute" });

    const oidcUser = useMemo(() => {
        try {
            const data = sessionStorage.getItem(SSO_STORAGE_KEY);
            return JSON.parse(data ?? "{}") as OIDCUser;
        } catch (error: unknown) {
            localLogger.fatal({ error }, "Error parsing employee profile");
            // TODO @Soluto-Private/expert-workspace-solve-devs, throw an error here
            return {};
        }
        // oxlint-disable-next-line exhaustive-deps
    }, []) as OIDCUser;

    useEffect(() => {
        if (!hasAuthParams() && !auth.isAuthenticated && !auth.isLoading && !auth.activeNavigator) {
            try {
                void auth.signinRedirect({ nonce: crypto.randomUUID() });
            } catch (error: unknown) {
                localLogger.fatal({ error }, "Auth error occurred");
                // TODO @Soluto-Private/expert-workspace-solve-devs, throw an error here
            }
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [auth]);

    useEffect(() => {
        if (auth.isAuthenticated && auth.user?.access_token) {
            setToken(auth.user.access_token);
        }
    }, [auth, setToken]);

    useEffect(() => {
        if (auth.isAuthenticated) {
            setExpertProfile(
                isE2EUser(oidcUser)
                    ? formatE2EUser(oidcUser)
                    : {
                          firstName: oidcUser.profile.given_name ?? "",
                          lastName: oidcUser.profile.family_name,
                          email: oidcUser.profile.email,
                          id: oidcUser.profile.employeeID,
                      },
            );
        }
    }, [auth, oidcUser, setExpertProfile]);

    switch (auth.activeNavigator) {
        case "signinRedirect":
        case "signinSilent":
            return <Navigate to={RoutePath.Landing} replace />;
        default:
            break;
    }

    //TODO: what causes this kind of error and how do we want to handle it?
    if (auth.error) {
        return <div>Auth Error Occurred</div>;
    }
    if (auth.isLoading || !auth.user?.access_token) return <GlobalLoader msg="Loading Expert Assist..." />;

    return <>{auth.isAuthenticated && props.children}</>;
}
